import { logger, sanitizeError } from '@heidi/runtime/logger';
import { LetterDeliveryStatus, LetterEditableStatus } from '@heidi/schema/ml-scribe';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { SEND_LETTER } from '../../contracts/queryKeys';
import { sendLetter } from '../api/session-letters';
import { lettersQueryOptions, type QueryLettersResponse } from '../query/useQueryLetters';

type UseMutateSendLetterParams = {
  sessionId: string;
};

type SendLetterMutationInput = {
  letterId: string;
  pdfFile: File;
};

export function useMutateSendLetter({ sessionId }: UseMutateSendLetterParams) {
  const queryClient = useQueryClient();
  const { queryKey } = lettersQueryOptions(sessionId);

  return useMutation({
    mutationKey: [SEND_LETTER, { sessionId }],
    mutationFn: async ({ letterId, pdfFile }: SendLetterMutationInput) => {
      const response = await sendLetter({ sessionId, letterId, pdfFile });
      return response.data;
    },
    onSuccess: (data, { letterId }) => {
      const sentAt = new Date().toISOString();
      const isDelivered =
        data.delivery_status === LetterDeliveryStatus.SENT ||
        data.delivery_status === LetterDeliveryStatus.PARTIALLY_SENT;

      queryClient.setQueryData<QueryLettersResponse>(queryKey, (current) => {
        if (!current) return current;

        return current.map((letter) =>
          letter.id === letterId
            ? ({
                ...letter,
                is_sent: isDelivered,
                sent: isDelivered,
                sent_at: isDelivered ? sentAt : undefined,
                status: data.delivery_status?.toLowerCase(),
                delivery_status: data.delivery_status,
                delivery_details: data.delivery_details,
                editable_status: data.editable_status ?? LetterEditableStatus.FREE,
              } as typeof letter)
            : letter
        );
      });

      void queryClient.invalidateQueries({ queryKey });
    },
    onError: (error) => {
      logger.error('[LetterSender] Failed to send letter:', sanitizeError(error));
    },
  });
}
